import type { CandidateRecord } from '../types/market.js';

export const PREDICTION_RANK_HOLD_MS = 10_000;
export const PREDICTION_RANK_OVERTAKE_MARGIN = 2;

export function computeStablePredictorOrder(
  candidates: CandidateRecord[],
  previousOrder: string[],
  lastReflowAt: number,
  now: number,
): { rows: CandidateRecord[]; order: string[]; reflowAt: number } {
  const byId = new Map(candidates.map((candidate) => [candidate.id, candidate]));
  const natural = [...candidates].sort(
    (left, right) =>
      (right.priorityScore ?? right.candidateScore) - (left.priorityScore ?? left.candidateScore) ||
      right.candidateScore - left.candidateScore ||
      right.movementStrength - left.movementStrength ||
      left.id.localeCompare(right.id),
  );
  const naturalIds = natural.map((candidate) => candidate.id);
  const canReflow = lastReflowAt === 0 || now - lastReflowAt >= PREDICTION_RANK_HOLD_MS;
  let order = canReflow || previousOrder.length === 0
    ? naturalIds
    : previousOrder.filter((id) => byId.has(id));
  if (!canReflow && previousOrder.length > 0) {
    order = [...order, ...naturalIds.filter((id) => !order.includes(id))];
    for (let index = 1; index < order.length; index += 1) {
      const id = order[index]!;
      const aboveId = order[index - 1]!;
      const current = byId.get(id);
      const above = byId.get(aboveId);
      if (!current || !above) continue;
      const currentPriority = current.priorityScore ?? current.candidateScore;
      const abovePriority = above.priorityScore ?? above.candidateScore;
      if (currentPriority >= abovePriority + PREDICTION_RANK_OVERTAKE_MARGIN) {
        order[index - 1] = id;
        order[index] = aboveId;
      }
    }
  }
  return {
    rows: order.map((id) => byId.get(id)).filter((candidate): candidate is CandidateRecord => Boolean(candidate)),
    order,
    reflowAt: canReflow ? now : lastReflowAt,
  };
}

export function mergeIssuedPredictions(
  liveRows: CandidateRecord[],
  issuedPredictions: Map<string, CandidateRecord>,
  now: number,
): CandidateRecord[] {
  const rowMap = new Map(liveRows.map((row) => [row.id, row]));
  for (const candidate of liveRows) {
    if (candidate.predictionIssuedAt !== undefined) issuedPredictions.set(candidate.id, candidate);
  }
  const latestEpisodeByMarket = new Map<string, CandidateRecord>();
  for (const row of liveRows) {
    const key = `${row.symbol}:${row.horizon}`;
    const current = latestEpisodeByMarket.get(key);
    if (!current || row.foundAt > current.foundAt) latestEpisodeByMarket.set(key, row);
  }
  for (const [id, candidate] of issuedPredictions) {
    if (now >= candidate.expiresAt) {
      issuedPredictions.delete(id);
      continue;
    }
    const newerEpisode = latestEpisodeByMarket.get(`${candidate.symbol}:${candidate.horizon}`);
    if (newerEpisode && newerEpisode.id !== id && newerEpisode.foundAt > candidate.foundAt) {
      // A reversal/new episode takes over the visible slot. The old episode
      // remains in the archive rather than creating two simultaneous predictions.
      issuedPredictions.delete(id);
      continue;
    }
    if (!rowMap.has(id)) {
      issuedPredictions.set(id, {
        ...candidate,
        predictionHeld: true,
      });
    }
  }
  for (const candidate of issuedPredictions.values()) if (!rowMap.has(candidate.id)) rowMap.set(candidate.id, candidate);
  return [...rowMap.values()];
}
